/** Utility functions for task augmentation logic, separated from React hook logic. */
// @ts-check
import { CONTEXT_SUGGESTION_CONFIDENCE } from '@/config/constants';

/**
 * @param {string[]} parsedProjects
 * @param {Map<string, string>} projectDisplayMap
 * @param {string[]} hiddenProjects
 * @returns {string[]}
 */
const extractFinalProjects = (
  parsedProjects,
  projectDisplayMap,
  hiddenProjects,
) => {
  if (parsedProjects.length > 0) return parsedProjects;
  if (hiddenProjects.length === 0) return [];
  const allProjectKeys = Array.from(projectDisplayMap.keys());
  const selectedProjects = allProjectKeys.filter(
    (p) => !hiddenProjects.includes(p),
  );
  return selectedProjects.length === 1 ? selectedProjects : [];
};

/**
 * @param {string[]} parsedContexts
 * @param {Map<string, string>} contextDisplayMap
 * @param {string[]} hiddenContexts
 * @returns {{ contexts: string[], filterBased: boolean }}
 */
const extractFinalContexts = (
  parsedContexts,
  contextDisplayMap,
  hiddenContexts,
) => {
  if (parsedContexts.length > 0)
    return { contexts: parsedContexts, filterBased: false };
  if (hiddenContexts.length === 0)
    return { contexts: [], filterBased: false };
  const allContextKeys = Array.from(contextDisplayMap.keys());
  const selectedContexts = allContextKeys.filter(
    (c) => !hiddenContexts.includes(c),
  );
  if (selectedContexts.length === 1) {
    return { contexts: selectedContexts, filterBased: true };
  }
  return { contexts: [], filterBased: false };
};

/**
 * @param {any} payload
 * @param {Task[]} allTasks
 */
const inheritParentStatus = (payload, allTasks) => {
  const parentId = payload.parentId;
  if (parentId && allTasks) {
    const parentTask = allTasks.find((task) => task.id === parentId);
    if (parentTask && parentTask.status === 'Backlog') {
      payload.status = 'Backlog';
    }
  }
};

/**
 * @param {string[]} projectIds
 * @param {Task[]} allTasks
 * @returns {string | null}
 */
const suggestContextFromHistory = (projectIds, allTasks) => {
  const tasksWithProject = allTasks.filter(
    (task) =>
      task.projects && task.projects.some((p) => projectIds.includes(p)),
  );

  if (tasksWithProject.length < 5) return null;

  const contextCounts = new Map();
  let totalOccurrences = 0;

  tasksWithProject.forEach((task) => {
    (task.contexts || []).forEach((context) => {
      contextCounts.set(context, (contextCounts.get(context) || 0) + 1);
      totalOccurrences++;
    });
  });

  if (totalOccurrences === 0) return null;

  let mostCommon = null;
  let highestCount = 0;
  contextCounts.forEach((count, context) => {
    if (count > highestCount) {
      highestCount = count;
      mostCommon = context;
    }
  });

  if (
    mostCommon &&
    highestCount / totalOccurrences >= CONTEXT_SUGGESTION_CONFIDENCE
  ) {
    return mostCommon;
  }
  return null;
};

/**
 * @param {any} taskPayload
 * @param {Task[]} allTasks
 * @param {Map<string, string>} projectDisplayMap
 * @param {string[]} hiddenProjects
 * @param {Map<string, string>} contextDisplayMap
 * @param {string[]} hiddenContexts
 * @returns {any}
 */
export const augmentTask = (
  taskPayload,
  allTasks,
  projectDisplayMap,
  hiddenProjects,
  contextDisplayMap,
  hiddenContexts,
) => {
  const {
    projects: parsedProjects = [],
    contexts: parsedContexts = [],
    ...restOfPayload
  } = taskPayload;

  const isSubtask = Boolean(restOfPayload.parentId);

  const finalProjects = isSubtask
    ? parsedProjects
    : extractFinalProjects(
        parsedProjects,
        projectDisplayMap,
        hiddenProjects,
      );
  const { contexts: baseContexts, filterBased: filterBasedContext } = isSubtask
    ? { contexts: parsedContexts, filterBased: false }
    : extractFinalContexts(parsedContexts, contextDisplayMap, hiddenContexts);

  inheritParentStatus(restOfPayload, allTasks);

  let finalContexts = baseContexts;
  if (
    !isSubtask &&
    !filterBasedContext &&
    finalContexts.length === 0 &&
    finalProjects.length > 0 &&
    allTasks
  ) {
    const suggested = suggestContextFromHistory(finalProjects, allTasks);
    if (suggested) finalContexts = [suggested];
  }

  return { ...restOfPayload, projects: finalProjects, contexts: finalContexts };
};
